import { FormEvent, useState } from "react";
import { FileImage, Send } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import {
  createChamado,
  type ChamadoDto,
  type ChamadoTipo,
} from "@/services/chamados";

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated?: (chamado: ChamadoDto) => void;
}

interface FormState {
  titulo: string;
  tipo: ChamadoTipo | "";
  modulo: string;
  descricao: string;
  pathname: string;
  userAgent: string;
  print: File | null;
}

const INITIAL_FORM: FormState = {
  titulo: "",
  tipo: "",
  modulo: "",
  descricao: "",
  pathname: "",
  userAgent: "",
  print: null,
};

function initialFormWithContext(): FormState {
  return {
    ...INITIAL_FORM,
    pathname: window.location.pathname,
    userAgent: window.navigator.userAgent,
  };
}

const TIPO_OPTIONS = [
  { value: "bug", label: "Bug" },
  { value: "duvida", label: "Dúvida" },
  { value: "acesso", label: "Acesso" },
  { value: "sugestao", label: "Sugestão" },
];

const MODULO_OPTIONS = [
  { value: "Locações", label: "Locações" },
  { value: "Financeiro", label: "Financeiro" },
  { value: "Solicitação de Pagamento", label: "Solicitação de Pagamento" },
  { value: "Suprimentos", label: "Suprimentos" },
  { value: "Tarefas", label: "Tarefas" },
  { value: "Chão de Obra", label: "Chão de Obra" },
  { value: "Comercial", label: "Comercial" },
  { value: "Orçamento", label: "Orçamento" },
  { value: "Gerencial", label: "Gerencial" },
  { value: "Acessos", label: "Acessos" },
  { value: "Outro", label: "Outro" },
];

function errorMessage(error: unknown): string {
  if (error && typeof error === "object" && "response" in error) {
    const response = (error as { response?: { data?: { error?: unknown } } }).response;
    if (typeof response?.data?.error === "string") return response.data.error;
  }
  return error instanceof Error ? error.message : "Não foi possível abrir o chamado.";
}

export function ModalNovoChamado({ open, onClose, onCreated }: Props) {
  const [form, setForm] = useState<FormState>(initialFormWithContext);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    if (!submitting) onClose();
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");

    if (!form.titulo.trim() || !form.tipo || !form.modulo || !form.descricao.trim()) {
      setError("Preencha título, tipo, módulo e descrição.");
      return;
    }
    if (form.print && form.print.size > 10 * 1024 * 1024) {
      setError("O print deve ter no máximo 10 MB.");
      return;
    }

    setSubmitting(true);
    try {
      const chamado = await createChamado({
        titulo: form.titulo.trim(),
        tipo: form.tipo,
        modulo: form.modulo,
        descricao: form.descricao.trim(),
        pathname: form.pathname,
        userAgent: form.userAgent,
        print: form.print,
      });
      onCreated?.(chamado);
      onClose();
    } catch (submitError) {
      setError(errorMessage(submitError));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Abrir chamado de TI"
      size="md"
      closeOnEscape
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={handleClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            type="submit"
            form="novo-chamado-form"
            loading={submitting}
            leftIcon={<Send className="h-4 w-4" />}
          >
            Enviar chamado
          </Button>
        </div>
      }
    >
      <form id="novo-chamado-form" className="space-y-4" onSubmit={handleSubmit}>
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}

        <Input
          label="Título do problema"
          value={form.titulo}
          maxLength={160}
          autoFocus
          onChange={(event) => setForm((current) => ({ ...current, titulo: event.target.value }))}
          placeholder="Resuma o que aconteceu"
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            label="Tipo"
            value={form.tipo}
            options={TIPO_OPTIONS}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                tipo: event.target.value as ChamadoTipo | "",
              }))
            }
          />
          <Select
            label="Módulo"
            value={form.modulo}
            options={MODULO_OPTIONS}
            onChange={(event) =>
              setForm((current) => ({ ...current, modulo: event.target.value }))
            }
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="chamado-descricao"
            className="text-xs font-semibold uppercase tracking-wide text-slate-500"
          >
            Descrição detalhada
          </label>
          <textarea
            id="chamado-descricao"
            rows={5}
            maxLength={10_000}
            value={form.descricao}
            onChange={(event) =>
              setForm((current) => ({ ...current, descricao: event.target.value }))
            }
            placeholder="Descreva os passos, o resultado esperado e o que ocorreu."
            className="w-full resize-y rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 transition-colors placeholder:text-slate-400 hover:border-slate-300 focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="chamado-print"
            className="text-xs font-semibold uppercase tracking-wide text-slate-500"
          >
            Upload de print
          </label>
          <label
            htmlFor="chamado-print"
            className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-600 transition-colors hover:border-brand-400 hover:bg-brand-50"
          >
            <FileImage className="h-5 w-5 text-brand-600" />
            <span className="min-w-0 truncate">
              {form.print?.name ?? "Selecionar imagem (até 10 MB)"}
            </span>
          </label>
          <input
            id="chamado-print"
            className="sr-only"
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                print: event.target.files?.[0] ?? null,
              }))
            }
          />
        </div>
      </form>
    </Modal>
  );
}
